import { type UseFormReturn } from "react-hook-form";
import { AlertTriangle, ShieldAlert } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/Card";
import { cn } from "@/lib/cn";
import type { IntakeFormData } from "../hooks/useIntakeForm";

interface MentalHealthStepProps {
  form: UseFormReturn<IntakeFormData>;
}

export function MentalHealthStep({ form }: MentalHealthStepProps) {
  const {
    register,
    watch,
  } = form;

  const isSuicidal = watch("isSuicidal");

  return (
    <div className="space-y-6">
      {/* Disclaimer */}
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
        <div className="flex gap-3">
          <ShieldAlert className="h-5 w-5 shrink-0 text-amber-600" />
          <div className="text-sm text-amber-800">
            <p className="font-medium">Sensitive Information</p>
            <p className="mt-1">
              This section contains mental health screening information. Access
              to this data may be restricted based on agency configuration and
              user permissions. Handle in accordance with HIPAA and agency
              privacy policies.
            </p>
          </div>
        </div>
      </div>

      {/* Mental Health Screening */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Mental Health Screening</CardTitle>
          <CardDescription>
            Record mental health diagnosis, substance abuse, and suicidal
            ideation information.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">
              Mental Health Diagnosis
            </label>
            <textarea
              {...register("mentalHealthDiagnosis")}
              rows={4}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              placeholder="Enter known mental health diagnoses..."
            />
          </div>

          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                {...register("isSubstanceAbuse")}
                className="h-4 w-4 rounded accent-primary"
              />
              History of Substance Abuse
            </label>
            {watch("isSubstanceAbuse") && (
              <div className="space-y-1.5 pl-6">
                <label className="text-sm font-medium text-foreground">
                  Substance Abuse Details
                </label>
                <textarea
                  {...register("substanceAbuseDetails")}
                  rows={3}
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  placeholder="Describe substance abuse history and current status..."
                />
              </div>
            )}
          </div>

          {/* Suicidal Ideation - highlighted */}
          <div
            className={cn(
              "rounded-lg border p-4 transition-colors",
              isSuicidal
                ? "border-destructive bg-destructive/5"
                : "border-input",
            )}
          >
            <label
              className={cn(
                "flex items-center gap-2 text-sm font-medium",
                isSuicidal && "text-destructive",
              )}
            >
              <input
                type="checkbox"
                {...register("isSuicidal")}
                className="h-4 w-4 rounded accent-destructive"
              />
              <AlertTriangle className="h-4 w-4" />
              Suicidal Ideation
            </label>
            {isSuicidal && (
              <div className="mt-3 space-y-1.5">
                <label className="text-sm font-medium text-destructive">
                  Suicidal Ideation Details
                </label>
                <textarea
                  {...register("suicidalDetails")}
                  rows={3}
                  className="flex w-full rounded-md border border-destructive bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive"
                  placeholder="Describe suicidal ideation concerns, any plans, risk factors..."
                />
                <p className="text-xs text-destructive">
                  If client is at immediate risk, follow agency crisis protocol.
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Medications */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Medications</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">
              Current Medications
            </label>
            <textarea
              {...register("medications")}
              rows={4}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              placeholder="List all current medications, dosages, and prescribers..."
            />
          </div>
        </CardContent>
      </Card>

      {/* Mental Health Narrative */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Mental Health Narrative</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">
              Narrative
            </label>
            <textarea
              {...register("mentalHealthNarrative")}
              rows={6}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              placeholder="Additional mental health notes, observations, or screening results..."
            />
          </div>
        </CardContent>
      </Card>

      {/* Release Record */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Release of Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                {...register("releaseRecord")}
                className="h-4 w-4 rounded accent-primary"
              />
              <span className="font-medium">Release Record Authorized</span>
            </label>
            <span className="text-xs text-muted-foreground">
              Client has authorized release of mental health records
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
